import type { ExpeditionLoadout, ExpeditionRoute } from '../sim/expedition';
import type { StorageLike } from './levelStore';

export const STORAGE_KEY = 'jreb1d.expedition.v1';
export const MAX_DEPTH = 999_999;
const MAX_SOLDIERS = 10_000;
const MAX_TIER = 3;
const MAX_PIPS = 2;

type Status = 'ready' | 'debrief' | 'sector' | 'lost' | 'extracted';
export type ExpeditionRun = {
  seed: string; depth: number; route: ExpeditionRoute; loadout: ExpeditionLoadout;
  salvage: number; status: Status; lastReward: number;
};
export type ExpeditionSave = { version: 1; banked: number; bestDepth: number; run: ExpeditionRun };

export function createExpedition(seed: string): ExpeditionSave {
  return { version: 1, banked: 0, bestDepth: 0, run: {
    seed: seed.trim().slice(0, 64) || 'expedition', depth: 1, route: 'recruitment',
    loadout: { n: 20, weaponTier: 0, ratePips: 0 }, salvage: 0, status: 'ready', lastReward: 0,
  } };
}

export function newExpedition(save: ExpeditionSave, seed: string): ExpeditionSave {
  return { ...createExpedition(seed), banked: save.banked, bestDepth: save.bestDepth };
}

const bounded = (n: number, min: number, max: number) => Number.isFinite(n) ? Math.min(max, Math.max(min, Math.floor(n))) : min;
const add = (a: number, b: number) => Math.min(Number.MAX_SAFE_INTEGER, a + b);

export function resolveExpedition(save: ExpeditionSave, result: ExpeditionLoadout & { won: boolean }): ExpeditionSave {
  const run = save.run;
  if (run.status !== 'ready') return save;
  if (!result.won) {
    const reward = Math.floor(run.salvage * 0.25);
    return { ...save, banked: add(save.banked, reward), run: { ...run, salvage: 0, status: 'lost', lastReward: reward } };
  }
  const reward = 30 + 10 * run.depth;
  return { ...save, bestDepth: Math.max(save.bestDepth, run.depth), run: {
    ...run, loadout: { n: bounded(result.n, 20, MAX_SOLDIERS), weaponTier: bounded(result.weaponTier, 0, MAX_TIER), ratePips: bounded(result.ratePips, 0, MAX_PIPS) },
    salvage: add(run.salvage, reward), status: run.depth % 3 === 0 ? 'sector' : 'debrief', lastReward: reward,
  } };
}

export function routeRewardLabel(loadout: ExpeditionLoadout, route: ExpeditionRoute): string {
  if (route === 'armory' && loadout.weaponTier < MAX_TIER) return '+1 weapon tier';
  if (route === 'armory' && loadout.ratePips < MAX_PIPS) return '+1 fire-rate boost';
  const gained = Math.min(20, MAX_SOLDIERS - loadout.n);
  return gained > 0 ? `+${gained} soldiers` : 'Soldiers at capacity';
}

export function chooseExpeditionRoute(save: ExpeditionSave, route: ExpeditionRoute): ExpeditionSave {
  const run = save.run;
  if ((run.status !== 'debrief' && run.status !== 'sector') || run.depth >= MAX_DEPTH || (route !== 'recruitment' && route !== 'armory')) return save;
  const loadout = { ...run.loadout };
  if (route === 'armory' && loadout.weaponTier < MAX_TIER) loadout.weaponTier++;
  else if (route === 'armory' && loadout.ratePips < MAX_PIPS) loadout.ratePips++;
  else loadout.n = Math.min(MAX_SOLDIERS, loadout.n + 20);
  return { ...save, run: { ...run, depth: run.depth + 1, route, loadout, status: 'ready', lastReward: 0 } };
}

export function extractExpedition(save: ExpeditionSave): ExpeditionSave {
  if (save.run.status !== 'sector') return save;
  const reward = save.run.salvage;
  return { ...save, banked: add(save.banked, reward), run: { ...save.run, salvage: 0, status: 'extracted', lastReward: reward } };
}

const integer = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
const record = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
export function parseExpeditionSave(raw: string | null): ExpeditionSave | null {
  if (raw === null || raw.length > 4096) return null;
  try {
    const s: unknown = JSON.parse(raw);
    if (!record(s) || s.version !== 1 || !integer(s.banked, 0, Number.MAX_SAFE_INTEGER) || !integer(s.bestDepth, 0, MAX_DEPTH) || !record(s.run)) return null;
    const r = s.run;
    if (typeof r.seed !== 'string' || r.seed.length < 1 || r.seed.length > 64 || r.seed !== r.seed.trim() ||
      !integer(r.depth, 1, MAX_DEPTH) || (r.route !== 'recruitment' && r.route !== 'armory') || !record(r.loadout) ||
      !integer(r.loadout.n, 20, MAX_SOLDIERS) || !integer(r.loadout.weaponTier, 0, MAX_TIER) || !integer(r.loadout.ratePips, 0, MAX_PIPS) ||
      !integer(r.salvage, 0, Number.MAX_SAFE_INTEGER) || !integer(r.lastReward, 0, Number.MAX_SAFE_INTEGER) ||
      (r.status !== 'ready' && r.status !== 'debrief' && r.status !== 'sector' && r.status !== 'lost' && r.status !== 'extracted') ||
      (r.status === 'sector' && r.depth % 3 !== 0) || (r.status === 'debrief' && r.depth % 3 === 0) ||
      ((r.status === 'lost' || r.status === 'extracted') && r.salvage !== 0) || (r.status === 'ready' && r.lastReward !== 0)) return null;
    return { version: 1, banked: s.banked, bestDepth: s.bestDepth, run: { seed: r.seed, depth: r.depth, route: r.route,
      loadout: { n: r.loadout.n, weaponTier: r.loadout.weaponTier, ratePips: r.loadout.ratePips }, salvage: r.salvage, status: r.status, lastReward: r.lastReward } };
  } catch { return null; }
}

export function createExpeditionStore(get: () => StorageLike = () => localStorage) {
  let memory: ExpeditionSave | null = null;
  let persistent = true;
  return {
    get persistent() { return persistent; },
    read(): ExpeditionSave | null {
      if (!persistent) return memory;
      try { memory = parseExpeditionSave(get().getItem(STORAGE_KEY)); return memory; }
      catch { persistent = false; return memory; }
    },
    write(save: ExpeditionSave): void {
      memory = save;
      if (!persistent) return;
      try { get().setItem(STORAGE_KEY, JSON.stringify(save)); }
      catch { persistent = false; }
    },
  };
}
